"use client";

import React, { useEffect, useState } from "react";
import { Search, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription, SheetHeader, SheetClose } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
function getSafeMenuHref(href?: string | null) {
  if (!href) return null;
  const cleanHref = href.trim();
  if (!cleanHref || cleanHref === "#") return null;
  return cleanHref;
}
function formatProductPrice(price?: string | number | null) {
  if (price === null || price === undefined) return "Ver precio";
  const normalized = String(price).trim();
  if (!normalized) return "Ver precio";
  if (/^\d+([.,]\d+)?$/.test(normalized)) {
    return `$${normalized} MXN`;
  }
  if (normalized.includes("€")) {
    return normalized.replace(/€/g, "$").replace(/\bEUR\b/gi, "MXN");
  }
  return normalized;
}
function SearchResults() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="26377" className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-48 animate-pulse rounded-none bg-muted" />)}
      </div>;
  }
  if (error) {
    return <p className="font-sans text-sm text-destructive">
        Error al cargar los productos.
      </p>;
  }
  if (!products.length) {
    return <p className="font-sans text-sm text-muted-foreground">
        No se encontraron productos.
      </p>;
  }
  return <div className="grid grid-cols-2 gap-6 md:grid-cols-4 lg:grid-cols-5">
      {products.slice(0, 5).map((product, i) => <Link key={product.id} to={product.link} className="group flex flex-col gap-2" data-index={i}>
          <div className="aspect-[3/4] w-full overflow-hidden rounded-none bg-muted transition-shadow duration-500 group-hover:shadow-lg">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-secondary/20 font-serif italic text-muted-foreground">
                Sin imagen
              </div>}
          </div>

          <div>
            <h3 className="truncate font-sans text-sm font-medium tracking-wide text-foreground">
              {product.title}
            </h3>
            <p className="font-sans text-xs text-muted-foreground">
              {formatProductPrice(product.price)}
            </p>
          </div>
        </Link>)}
    </div>;
}
function SearchSheet() {
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  return <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="transition-colors hover:bg-transparent hover:text-accent" aria-label="Buscar en la tienda">
          <Search className="size-5 text-foreground" />
          <span className="sr-only">Buscar en la tienda</span>
        </Button>
      </SheetTrigger>

      <SheetContent side="top" className="h-auto min-h-[300px] w-full border-b border-accent/20 bg-background">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl tracking-wide text-primary">
            Buscar en la tienda
          </SheetTitle>
          <SheetDescription className="font-sans text-muted-foreground">
            Encuentra cuarzos, velas, inciensos, joyería y piezas para tus rituales.
          </SheetDescription>
        </SheetHeader>

        <div className="mx-auto mt-6 max-w-5xl space-y-8">
          <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Cuarzos, velas, inciensos, rituales..." className="w-full border-b border-accent/30 font-sans transition-colors focus-within:border-accent" />

          <div className="min-h-[200px]">
            {hasSearchQuery ? <EcommerceProductsProvider wp_query={wp_query}>
                <SearchResults />
              </EcommerceProductsProvider> : <div className="flex h-32 items-center justify-center font-serif text-sm italic tracking-wide text-muted-foreground">
                Escribe para explorar la tienda...
              </div>}
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="h-4 w-32 animate-pulse rounded-none bg-muted" />;
  }
  if (!menuItems?.length) {
    return null;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => {
        const safeHref = getSafeMenuHref(item.href);
        return <NavigationMenuItem key={item.id} data-index={i}>
              {safeHref ? <NavigationMenuLink asChild>
                  <Link to={safeHref} className="font-serif text-xs font-medium uppercase tracking-[0.2em] text-foreground transition-all duration-300 hover:text-accent">
                    {item.label}
                  </Link>
                </NavigationMenuLink> : <span className="cursor-default font-serif text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground/70">
                  {item.label}
                </span>}
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-4 p-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-6 w-full animate-pulse rounded-none bg-muted" />)}
      </div>;
  }
  if (!menuItems?.length) {
    return null;
  }
  return <nav className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => {
      const safeHref = getSafeMenuHref(item.href);
      if (!safeHref) {
        return <span key={item.id} className="cursor-default font-serif text-xl font-light tracking-widest text-muted-foreground/70" data-index={i}>
              {item.label}
            </span>;
      }
      return <SheetClose asChild key={item.id}>
            <Link to={safeHref} className="font-serif text-xl font-light tracking-widest text-foreground transition-colors hover:text-accent" data-index={i}>
              {item.label}
            </Link>
          </SheetClose>;
    })}
    </nav>;
}
export default function CabeceraDeMarca() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (path === "/" || path === window.location.pathname) {
          hash = `#${hashPart}`;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="cabecera-mistica" className="relative z-50 bg-background text-foreground">
      <header className={`sticky top-0 z-50 w-full border-b border-transparent transition-all duration-700 ease-in-out ${isScrolled ? "border-accent/20 bg-background/80 shadow-sm backdrop-blur-md" : "bg-background"}`}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid h-24 grid-cols-[1fr_auto_1fr] items-center">
            <div className="flex items-center justify-start">
              <div className="hidden lg:block">
                <MenuProvider menu_id="50">
                  <DesktopMenuConsumer />
                </MenuProvider>
              </div>

              <div className="lg:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="hover:bg-transparent" aria-label="Abrir menú">
                      <Menu className="size-6 text-primary" />
                      <span className="sr-only">Abrir menú</span>
                    </Button>
                  </SheetTrigger>

                  <SheetContent side="left" className="w-[300px] border-r border-accent/20 bg-background sm:w-[400px]">
                    <SheetHeader className="px-6 pt-8 text-left">
                      <SheetTitle className="font-serif text-2xl uppercase tracking-widest text-primary">
                        Menú
                      </SheetTitle>
                      <SheetDescription className="font-sans text-muted-foreground">
                        Explora las colecciones y encuentra la pieza ideal para ti.
                      </SheetDescription>
                    </SheetHeader>

                    <MenuProvider menu_id="50">
                      <MobileMenuConsumer />
                    </MenuProvider>
                  </SheetContent>
                </Sheet>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <Link to="/" className="block transition-all duration-500 hover:scale-105 hover:opacity-80" aria-label="Ir al inicio">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580544/img/el_atelier_de_nuna_logo.png" alt="El Atelier de NuNa" className="h-12 w-auto object-contain" />
              </Link>
            </div>

            <div className="flex items-center justify-end gap-2 sm:gap-4">
              <SearchSheet />
              <CartIcon variant="ghost" size="icon" iconSize="size-5" className="transition-colors hover:bg-transparent hover:text-accent" />
            </div>
          </div>
        </div>
      </header>
    </section>;
}